import BigDrawer from "@/components/Drawer/BigDrawer";
import { loadAllStaff } from "@/redux/rtk/features/hrm/user/userSlice";
import { DatePicker, Form, Input, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ContactEmailField from "./ContactEmailField";

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

// Robust parser: accepts dayjs, Date, or strings in common formats
const toDayjs = (val) => {
  if (!val) return null;
  if (dayjs.isDayjs(val)) return val;

  // Date object
  if (Object.prototype.toString.call(val) === "[object Date]") {
    const d = dayjs(val);
    return d.isValid() ? d : null;
  }

  // Try strict common formats
  const formats = [
    "YYYY-MM-DD HH:mm:ss",
    "YYYY-MM-DD",
    "YYYY/MM/DD",
    "DD-MM-YYYY",
    "DD/MM/YYYY",
    "MM-DD-YYYY",
    "MM/DD/YYYY",
  ];
  for (const f of formats) {
    const d = dayjs(val, f, true);
    if (d.isValid()) return d;
  }

  // Last fallback (non-strict parse)
  const d = dayjs(val);
  return d.isValid() ? d : null;
};

const ContactInformation = ({ createAs, form, data }) => {
  const { t } = useTranslation();
  const { contact: singleContact } = useSelector((state) => state.contact);
  const { list: staffList, loading: staffLoading } = useSelector(
    (s) => s.users
  );

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadAllStaff({ query: "all" }));
  }, [dispatch]);

  useEffect(() => {
    if (!singleContact) return;

    const dob = toDayjs(singleContact.dateOfBirth);
    form.setFieldsValue({
      ...singleContact,
      dateOfBirth: dob,
    });
  }, [singleContact, form]);

  return (
    <div className="grid grid-cols-2 gap-4">
      <Form.Item
        label={t("contact.firstName")}
        name="firstName"
        rules={[{ required: true, message: t("contact.firstNameRequired") || "First Name is Required." }]}>
        <Input placeholder={t("contact.firstName")} />
      </Form.Item>

      <Form.Item label={t("contact.lastName")} name="lastName">
        <Input placeholder={t("contact.lastName")} />
      </Form.Item>

      <ContactEmailField
        isUpdate={!!data?.id}
        form={form}
        name="email"
        className="w-full"
        initialSku={data?.email || ""}
      />

      <Form.Item label={t("contact.owner")} name="contactOwnerId">
        <Select
          style={{ width: "100%" }}
          loading={staffLoading}
          allowClear
          showSearch
          placeholder={t("contact.selectOwner") || "Select contact owner name"}>
          {staffList?.map((item) => (
            <Select.Option key={item.id} value={item.id}>
              {item?.username}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label={t("contact.birthday") || "Birthday"}
        name="dateOfBirth"
        getValueProps={(val) => ({ value: toDayjs(val) })}
        getValueFromEvent={(val) => (val ? toDayjs(val) : null)}>
        <DatePicker
          placeholder={t("contact.selectBirthday") || "Select birthday"}
          style={{ width: "100%" }}
          format="YYYY-MM-DD"
          allowClear
        />
      </Form.Item>



      <Form.Item label={t("contact.jobTitle") || "Job title"} name="jobTitle">
        <Input placeholder={t("contact.jobTitle") || "CEO"} />
      </Form.Item>

      <Form.Item label={t("common.phone")} name="phone">
        <Input placeholder={t("common.phone")} />
      </Form.Item>
      
      <Form.Item label={t("lead.mobile")} name="mobile">
        <Input placeholder={t("lead.mobile")} />
      </Form.Item>

      <Form.Item label={t("contact.department") || "Department"} name="department">
        <Input placeholder={t("contact.department") || "IT"} />
      </Form.Item>

    </div>
  );
};

export default ContactInformation;
